import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Flame, Trophy, CalendarCheck, Target, Shield, Award, Star } from 'lucide-react';
import axios from 'axios';
import { logError } from '../lib/errorLogger';
import StreakReminderBanner from './StreakReminderBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_ICONS = {
  streak_7: <Shield className="w-5 h-5" />,
  streak_30: <Award className="w-5 h-5" />,
  streak_100: <Star className="w-5 h-5" />,
};

const BadgeItem = ({ badge }) => {
  const icon = BADGE_ICONS[badge.id] || <Shield className="w-5 h-5" />;
  const earned = badge.earned;

  return (
    <div
      className={`relative flex flex-col items-center p-3 rounded-xl transition-all duration-300 ${
        earned ? 'bg-gradient-to-b from-[#4ADE80]/20 to-[#FF6B4A]/10' : 'bg-[#FFF2EC] opacity-60'
      }`}
      data-testid={`badge-${badge.id}`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
        earned ? 'bg-[#FF6B4A] text-white' : 'bg-[#E5D0C9] text-[#8C7A76]'
      }`}>
        {icon}
      </div>
      <p className={`text-xs font-semibold text-center ${earned ? 'text-[#1A1514]' : 'text-[#8C7A76]'}`}>
        {badge.name}
      </p>
      <p className="text-[10px] text-[#8C7A76] text-center mt-0.5">{badge.description}</p>
      {!earned && (
        <div className="w-full mt-2">
          <div className="h-1 bg-[#E5D0C9] rounded-full overflow-hidden">
            <div className="h-full bg-[#4ADE80] rounded-full transition-all duration-500" style={{ width: `${badge.progress}%` }} />
          </div>
          <p className="text-[9px] text-[#8C7A76] text-center mt-0.5">{badge.progress}%</p>
        </div>
      )}
      {earned && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#4ADE80] rounded-full flex items-center justify-center">
          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </span>
      )}
    </div>
  );
};

const StreakCard = () => {
  const [streak, setStreak] = useState(null);

  const fetchStreak = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/streak`, { withCredentials: true });
      setStreak(res.data);
    } catch (err) {
      logError('StreakCard', 'Failed to fetch streak', { error: err?.message });
    }
  }, []);

  useEffect(() => {
    fetchStreak();
  }, [fetchStreak]);

  if (!streak) return null;

  const getFlameColor = (streakDays) => {
    if (streakDays >= 7) return 'text-[#A388EE]';
    if (streakDays >= 3) return 'text-[#4ADE80]';
    return 'text-[#8C7A76]';
  };

  const flameColor = getFlameColor(streak.current_streak);
  const weekProgress = Math.min(100, (streak.this_week / 7) * 100);

  return (
    <Card className="card-elevated border-0" data-testid="streak-card">
      <CardContent className="p-6">
        {/* Streak header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-[#FFF2EC] flex items-center justify-center ${flameColor}`}>
              <Flame className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-[#8C7A76]">Current Streak</p>
              <p className="text-2xl font-bold text-[#1A1514]" data-testid="streak-count">
                {streak.current_streak} {streak.current_streak === 1 ? 'day' : 'days'}
              </p>
            </div>
          </div>
          {streak.logged_today && (
            <span className="text-xs font-semibold text-[#4ADE80] bg-[#4ADE80]/15 px-3 py-1 rounded-full" data-testid="logged-today-badge">
              Logged today
            </span>
          )}
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-[#FFF2EC] rounded-xl p-3 text-center">
            <Trophy className="w-4 h-4 text-[#A388EE] mx-auto mb-1" />
            <p className="text-lg font-bold text-[#1A1514]">{streak.longest_streak}</p>
            <p className="text-xs text-[#8C7A76]">Best streak</p>
          </div>
          <div className="bg-[#FFF2EC] rounded-xl p-3 text-center">
            <CalendarCheck className="w-4 h-4 text-[#FF6B4A] mx-auto mb-1" />
            <p className="text-lg font-bold text-[#1A1514]">{streak.this_week}/7</p>
            <p className="text-xs text-[#8C7A76]">This week</p>
          </div>
          <div className="bg-[#FFF2EC] rounded-xl p-3 text-center">
            <Target className="w-4 h-4 text-[#4ADE80] mx-auto mb-1" />
            <p className="text-lg font-bold text-[#1A1514]">{streak.total_logs}</p>
            <p className="text-xs text-[#8C7A76]">Total logs</p>
          </div>
        </div>

        {/* Weekly progress bar */}
        <div className="mb-5">
          <div className="flex justify-between text-xs text-[#8C7A76] mb-1">
            <span>Weekly goal</span>
            <span>{streak.this_week}/7 days</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${weekProgress}%` }} />
          </div>
        </div>

        {/* Achievement Badges */}
        {streak.badges && streak.badges.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#8C7A76] uppercase tracking-widest mb-3">Achievements</p>
            <div className="grid grid-cols-3 gap-3">
              {streak.badges.map((badge) => (
                <BadgeItem key={badge.id} badge={badge} />
              ))}
            </div>
          </div>
        )}

        {streak.current_streak >= 3 && (
          <p className="text-xs text-[#4ADE80] font-medium mt-4 text-center">
            {streak.current_streak >= 7 ? "You're on fire! Keep it going!" : "Great consistency! 7-day streak unlocks Week Warrior badge!"}
          </p>
        )}

        {/* Streak reminder banner + push opt-in */}
        <div className="mt-4">
          <StreakReminderBanner streak={streak} />
        </div>
      </CardContent>
    </Card>
  );
};

export default StreakCard;
